import { Injectable } from '@angular/core';

/**
 * Service for managing feature flags stored in localStorage.
 * Features are hidden by default unless explicitly enabled.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {

  // Define feature flag keys as constants
  private readonly FEATURE_FLAGS = {
    DEV_ENABLE_ACH: 'dev_enable_ach',
    DEV_ENABLE_DEVELOPER_MENU: 'dev_enable_developer_menu',
    // Add more feature flags here as needed
  } as const;

  constructor() {}

  /**
   * Check if a specific feature is enabled
   * @param featureName The name of the feature flag
   * @returns true if the feature is enabled (localStorage value is '1'), false otherwise
   */
  isFeatureEnabled(featureName: string): boolean {
    try {
      const value = localStorage.getItem(featureName);
      return value === '1';
    } catch (error) {
      // Handle cases where localStorage is not available (e.g., SSR)
      console.warn('localStorage not available:', error);
      return false;
    }
  }

  /**
   * Enable or disable a feature
   * @param featureName The name of the feature flag
   * @param enabled Whether to enable or disable the feature
   */
  setFeature(featureName: string, enabled: boolean): void {
    try {
      if (enabled) {
        localStorage.setItem(featureName, '1');
      } else {
        localStorage.removeItem(featureName);
      }
    } catch (error) {
      console.warn('Could not update localStorage:', error);
    }
  }

  /**
   * Get all defined feature flags and their current states
   * @returns Object with feature names as keys and boolean states as values
   */
  getAllFeatures(): { [key: string]: boolean } {
    const features: { [key: string]: boolean } = {};

    Object.entries(this.FEATURE_FLAGS).forEach(([key, value]) => {
      features[key] = this.isFeatureEnabled(value);
    });

    return features;
  }

  /**
   * Check if ACH features are enabled
   * @returns true if ACH features should be shown, false otherwise
   */
  isAchEnabled(): boolean {
    return this.isFeatureEnabled(this.FEATURE_FLAGS.DEV_ENABLE_ACH);
  }

  /**
   * Toggle ACH feature on/off
   * @param enabled Whether to enable or disable ACH features
   */
  setAchEnabled(enabled: boolean): void {
    this.setFeature(this.FEATURE_FLAGS.DEV_ENABLE_ACH, enabled);
  }

  /**
   * Get the raw feature flag key for ACH
   * Useful for debugging or direct localStorage access
   */
  getAchFlagKey(): string {
    return this.FEATURE_FLAGS.DEV_ENABLE_ACH;
  }

  /**
   * Check if Developer Menu is enabled
   * @returns true if Developer Menu should be shown, false otherwise
   *
   * To enable from browser console:
   *   localStorage.setItem('dev_enable_developer_menu', '1'); location.reload();
   * To disable:
   *   localStorage.removeItem('dev_enable_developer_menu'); location.reload();
   */
  isDevMenuEnabled(): boolean {
    return this.isFeatureEnabled(this.FEATURE_FLAGS.DEV_ENABLE_DEVELOPER_MENU);
  }

  /**
   * Toggle Developer Menu on/off
   * @param enabled Whether to enable or disable Developer Menu
   */
  setDevMenuEnabled(enabled: boolean): void {
    this.setFeature(this.FEATURE_FLAGS.DEV_ENABLE_DEVELOPER_MENU, enabled);
  }

  /**
   * Get the raw feature flag key for Developer Menu
   */
  getDevMenuFlagKey(): string {
    return this.FEATURE_FLAGS.DEV_ENABLE_DEVELOPER_MENU;
  }

  /**
   * Clear all feature flags (reset to defaults - all disabled)
   */
  clearAllFeatures(): void {
    try {
      Object.values(this.FEATURE_FLAGS).forEach(flag => {
        localStorage.removeItem(flag);
      });
    } catch (error) {
      console.warn('Could not clear feature flags:', error);
    }
  }

  /**
   * Check if we're in a browser environment (not SSR)
   */
  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }
}